import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";
import { Separator } from "./separator"; // separator.tsx exists with lowercase
import {
  ChevronRight,
  ChevronLeft,
  BookOpen,
  MoreHorizontal,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useDocsDrawer } from "src/contexts/DocsDrawerContext";
import { getDocsSlugForPath, DOCS_BASE_URL } from "src/config/docs-mapping";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "src/hooks/useBreadcrumbs";

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  iconBgColor?: string;
  actions?: React.ReactNode;
  breadcrumbs?: Breadcrumb[];
  className?: string;
  children?: React.ReactNode;
  /** Override the auto-detected docs slug for this page */
  docsSlug?: string;
  /** Hide the help button on this page */
  hideHelp?: boolean;
}

export function PageHeader({
  title,
  description,
  icon: Icon,
  iconBgColor = "from-primary-500 to-primary-600",
  actions,
  breadcrumbs,
  className,
  children,
  docsSlug,
  hideHelp = false,
}: PageHeaderProps) {
  const location = useLocation();
  const { openDocs, mounted } = useDocsDrawer();
  const resolvedSlug = docsSlug || getDocsSlugForPath(location.pathname);
  const hasDocsPage = !!resolvedSlug;
  const { t } = useTranslation();
  const autoBreadcrumbs = useBreadcrumbs();

  // If explicit breadcrumbs provided, use those; otherwise auto-generate from module registry
  const resolvedBreadcrumbs: Breadcrumb[] = React.useMemo(() => {
    if (breadcrumbs && breadcrumbs.length > 0) return breadcrumbs;
    if (autoBreadcrumbs.length === 0) return [];
    return autoBreadcrumbs.map((crumb, i) => ({
      label: crumb.label,
      href: i < autoBreadcrumbs.length - 1 ? crumb.path : undefined,
    }));
  }, [breadcrumbs, autoBreadcrumbs]);

  // Mobile truncation: if more than 3 segments, show first + "..." + last two
  const displayBreadcrumbs: Breadcrumb[] = React.useMemo(() => {
    if (resolvedBreadcrumbs.length <= 3) return resolvedBreadcrumbs;
    return [
      resolvedBreadcrumbs[0],
      { label: "…" },
      ...resolvedBreadcrumbs.slice(-2),
    ];
  }, [resolvedBreadcrumbs]);

  const isRtl = document.dir === "rtl";
  const SeparatorIcon = isRtl ? ChevronLeft : ChevronRight;

  const handleOpenDocs = () => {
    if (mounted) {
      openDocs(resolvedSlug || undefined);
    } else if (resolvedSlug) {
      window.open(
        `${DOCS_BASE_URL}${resolvedSlug}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  };
  return (
    <div className={cn("space-y-3 px-4 sm:px-6 lg:px-8 py-4", className)}>
      {/* Breadcrumbs */}
      {displayBreadcrumbs.length > 0 && (
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {displayBreadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {crumb.href ? (
                <Link
                  to={crumb.href}
                  className="hover:text-foreground transition-colors"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={
                    index === displayBreadcrumbs.length - 1
                      ? "text-foreground font-medium"
                      : ""
                  }
                >
                  {crumb.label}
                </span>
              )}
              {index < displayBreadcrumbs.length - 1 && (
                <SeparatorIcon className="h-3.5 w-3.5" />
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {Icon && (
            <div
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center",
                // iconBgColor,
              )}
            >
              <Icon className="h-8 w-8 text-primary-600" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {description && (
              <p className="text-sm text-muted-foreground mt-0.5 truncate">
                {description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          {!hideHelp && hasDocsPage && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenDocs}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <BookOpen className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("help", "Help")}</span>
            </Button>
          )}
        </div>
      </div>
      {children && <div>{children}</div>}
      <Separator />
    </div>
  );
}
